import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import {type ApplicationContextInterface} from '@@/package/application/applicationContext';
import type SubscriptionApi from '~~/package/apis/subscription/SubscriptionApi';
import {SubscriptionModel} from '~~/package/mapper/subscription/model/SubscriptionModel';
import PlanModel from '~~/package/mapper/subscription/model/PlanModel';
import type CustomResponse from '~~/package/types/ResponseType';
import {PageModel} from '~~/package/mapper/common/model/PageModel';
import {SubscriptionHistoryModel} from '~~/package/mapper/subscription/model/SubscriptionHistoryModel';
import type PaymentHistoryResponse from '~~/package/apis/subscription/response/PaymentHistoryResponse';
import type CreditPricingResponse from '~~/package/apis/subscription/response/CreditPricingResponse';

export default class SubscriptionMapper {
    protected subscriptionApi: SubscriptionApi;

    constructor(context: ApplicationContextInterface) {
        this.subscriptionApi = context.getService<SubscriptionApi>(DependencyInjectId.SubscriptionApi);
    }

    public async getCurrentSubscription(): Promise<SubscriptionModel | null> {
        const response = await this.subscriptionApi.getCurrentSubscription();
        if (!response.data) {
            return null;
        }
        return SubscriptionModel.create(response.data);
    }

    public async getPlans(): Promise<{ plans: PlanModel[]; country: string | null }> {
        const response = await this.subscriptionApi.getPlans();
        const { currency, country, plans } = response.data;
        return {
            plans: (plans ?? []).map(plan => PlanModel.create(plan, currency, country)),
            country: country ?? null,
        };
    }

    public async cancelSubscription(): Promise<SubscriptionModel> {
        const response = await this.subscriptionApi.cancelSubscription();
        return SubscriptionModel.create(response.data);
    }

    public async cancelCancellationRequest(): Promise<SubscriptionModel> {
        const response = await this.subscriptionApi.cancelCancellationRequest();
        return SubscriptionModel.create(response.data);
    }

    public async restartSubscription(): Promise<SubscriptionModel> {
        const response = await this.subscriptionApi.restartSubscription();
        return SubscriptionModel.create(response.data);
    }

    public async retryPayment(): Promise<SubscriptionModel> {
        const response = await this.subscriptionApi.retryPayment();
        return SubscriptionModel.create(response.data);
    }

    public async getCreditPricing(): Promise<CreditPricingResponse> {
        const response = await this.subscriptionApi.getCreditPricing();
        return response.data;
    }

    public async getPaymentHistory(page: number, size: number): Promise<PageModel<SubscriptionHistoryModel>> {
        const response: CustomResponse<PageModel<PaymentHistoryResponse>> = (await this.subscriptionApi.getPaymentHistory(page, size)).data;
        const pageData = response.data;
        return new PageModel<SubscriptionHistoryModel>(
            pageData.data.map(item => SubscriptionHistoryModel.create(item as unknown as Partial<SubscriptionHistoryModel>)),
            pageData.last,
            pageData.pageNumber,
            pageData.totalElementCount,
        );
    }
}
